import { AfterViewInit, Component, ElementRef, Injector, OnInit, ViewChild } from '@angular/core';
import { FormControlStatus } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import {
    AnnualReportBusinessModel,
    AnnualReportProvider,
    DEFAULT_GERMAN_DISPLAY_OPTIONS,
    DEFAULT_SIGNATURE_OPTIONS,
    DEFAULT_STYLE_OPTIONS,
    DEFAULT_SWISS_DISPLAY_OPTIONS,
    ISectionData,
    PrintUsingPdfTronDto,
    onUpdateAnnualReportFailed,
    onUpdateAnnualReportSuccessfully,
    printAnnualReportUsingPdfTron
} from '@modeso/aumico-lib-annualreport-fe';
import {
    AumicoCoreLayoutProvider,
    DateLocalePipe,
    EMPTY_ARRAY_LENGTH,
    FIRST_ELEMENT_INDEX,
    FormStatus,
    GICSCodesUtil,
} from '@modeso/aumico-lib-core-fe';
import { GetOrganizationDto, OrganizationProvider, OrganizationSettings } from '@modeso/aumico-lib-users-fe';
import { CPMMState } from '@modeso/aumico-lib-users-fe/src/lib/enums/cpmm-state.enum';
import {
    AnnualReportStep,
    IAnnualReport,
    IComplementaryReport,
    IDisplayOptions,
    IFinStatements,
    IPrintOptions,
    IStyleOptions,
} from '@modeso/types__aumico-lib-annualreport-be';
import { IDynamicKeyValue } from '@modeso/types__aumico-lib-core-be';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { ofType } from '@ngrx/effects';
import { ActionsSubject } from '@ngrx/store';
import { I18NextService } from 'angular-i18next';
import fastDeepEqual from 'fast-deep-equal/es6';
import { cloneDeep } from 'lodash-es';
import { Observable, of } from 'rxjs';
import { filter, find, map, mergeMap, tap } from 'rxjs/operators';
import { DEFAULT_AR_NAVIGATION } from '../../../../../../aumico-lib-annualreport-fe/src/lib/constants/default-ar-navigation';
import { MixpanelService } from '../../../../../../mixpanel/mixpanel.service';
import { MIXPANEL_EVENTS } from '../../../../../../mixpanel/mixpanelEvents.enum';
import { environment } from '../../../../environments/environment';
import { IPdfTronMapper } from '../../../interfaces/pdf-tron-mapper.interface';
import { localizationKeys } from '../../../utils/localizationKeys';
import { AnnualReportSubPage } from '../annual-report-sub-page';
import { PdfTronGermanMapper } from '../mappers/pdf-tron-german-mapper';
import { PdfTronSwissMapper } from '../mappers/pdf-tron-swiss-mapper';
import { AnnualReportVisitor } from '../print-page/visitors/AnnualReportsVisitor';
import { PlaceholderReplacerVisitor } from '../print-page/visitors/PlaceholderReplacerVisitor';
import { AopSnapshotService } from '../services/aop-snapshot.service';
import { ComplementaryReportPrintService } from '../services/complementary-report-print.service';
import { LOADING_SPINNER_STYLE } from '../utils/loading-spinner-style';

@UntilDestroy()
@Component({
    selector: 'aumico-annual-report-complementary-reports-page',
    templateUrl: 'complementary-reports.page.html',
    styleUrls: ['complementary-reports.page.scss'],
})
export class ComplementaryReportsPage extends AnnualReportSubPage implements OnInit, AfterViewInit {
    @ViewChild('aopSnapshot', { static: false }) aopSnapshotRef: ElementRef;
    @ViewChild('aopSnapshotCurrentYear', { static: false }) aopSnapshotCurrentYearRef: ElementRef;
    public localizationKeys = localizationKeys;

    public currentLanguage = this.getCurrentLanguageWithFallBack();
    readonly defaultLanguage = environment.defaultLanguage;

    public currentAnnualReport: IAnnualReport | undefined;
    public currentComplementaryReports: IComplementaryReport[] = [];
    private complementaryReportsToBeSaved: IComplementaryReport[] = [];
    private currentFinStatements: IFinStatements[] | undefined;
    private currentOrganization: GetOrganizationDto | undefined;
    private organizationSettings: OrganizationSettings | undefined;
    private printWindow: Window | null = null;
    private placeholderMap: IDynamicKeyValue = {};

    public aopSnapshotHtmlContent: string | undefined;
    public aopSnapshotCurrentYearHtmlContent: string | undefined;
    public appropriationOfProfitSection: ISectionData | undefined;
    public isGermanBusinessModel = false;
    public snapshotStyleOptions: IStyleOptions | undefined;
    public snapshotNoPreviousData = false;
    private viewInitialized = false;

    private isAutofillButtonClicked = false;

    private isSaveRequestInProgress = false;

    isPartOfArNavigation$: Observable<boolean>;
    public organizationHasCpmmActive = false;

    constructor(
        injector: Injector,
        dialog: MatDialog,
        private annualReportProvider: AnnualReportProvider,
        protected override actionsListener$: ActionsSubject,
        protected override aumicoCoreLayoutProvider: AumicoCoreLayoutProvider,
        private readonly mixpanelService: MixpanelService,
        private readonly organizationProvider: OrganizationProvider,
        private readonly dateLocalePipe: DateLocalePipe,
        private readonly i18next: I18NextService,
        private readonly pdfTronGermanMapper: PdfTronGermanMapper,
        private readonly pdfTronSwissMapper: PdfTronSwissMapper,
        private readonly aopSnapshotService: AopSnapshotService,
        private readonly complementaryReportPrintService: ComplementaryReportPrintService,
    ) {
        super(injector, actionsListener$, aumicoCoreLayoutProvider, dialog);

        this.currentAnnualReportStep = AnnualReportStep.CR;

        this.isPartOfArNavigation$ = this.annualReportNavigationProvider.isPartOfArNavigation$(AnnualReportStep.CR);
    }

    override ngOnInit(): void {
        this.mixpanelService.time_event(MIXPANEL_EVENTS.COMPLEMENTARY_REPORTS_TIME_SPENT_TILL_COMPLETION);

        super.ngOnInit();

        this.annualReportProvider
            .getActiveAnnualReportEncryptedId$()
            .pipe(
                find((activeAnnualReportEncryptedId) => activeAnnualReportEncryptedId != null),
                untilDestroyed(this),
            )
            .subscribe((activeAnnualReportEncryptedId) => {
                this.annualReportProvider.dispatchActiveAnnualReport(activeAnnualReportEncryptedId);
            });

        this.disableProceedButton = false;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = false;

        // Subscribe to organization CPMM status
        this.organizationProvider
            .getCurrentOrganization$()
            .pipe(untilDestroyed(this))
            .subscribe((org) => {
                if (org == null) {
                    return;
                }
                this.currentOrganization = org;
                this.organizationHasCpmmActive = org.cpmmActivationDetails?.activationState === CPMMState.ACTIVE;
                this.organizationSettings = { ...this.organizationSettings, ...org.settings };
                this.refreshAopSnapshotData();
            });


        this.annualReportProvider
            // REV-20899: FAS / complementary reports must read the
            // reclass-applied finStatements so leaf accounts carry their
            // computed `closingReClass` / `openingReClass`, AND so phantom
            // reclasses accounts on `fli.reclassesAccounts` are present in
            // the snapshot. Using the raw selector here hid both from FAS.
            .getActiveFinStatementsWithReclasses$()
            .pipe(filter((finStatements) => finStatements != null), untilDestroyed(this))
            .subscribe((finStatements) => {
                this.currentFinStatements = cloneDeep(finStatements);
                this.refreshAopSnapshotData();
            });

        this.annualReportProvider
            // REV-20899 save-side isolation: this whole-report object is persisted
            // verbatim on save, so it MUST be the RAW report. The bridged
            // finStatements would zero a v1 report's inherited legacy reclasses.
            // The DISPLAY finStatements (currentFinStatements above) stay reclass-aware.
            .getActiveAnnualReport$()
            .pipe(
                filter(
                    (activeAnnualReport) => activeAnnualReport != null && activeAnnualReport.generalInformation != null,
                ),
                tap((activeAnnualReport) => this.handleGetActiveAnnualReport(activeAnnualReport)),

                filter(
                    (activeAnnualReport) =>
                        activeAnnualReport.generalInformation != null &&
                        (activeAnnualReport.generalInformation.isAutoFillFromPreviousAnnualReportEnabled ||
                            activeAnnualReport.generalInformation.isPreviousDataTakenFromPreviousAnnualReport) &&
                        activeAnnualReport.complementaryReports == null,
                ),
                mergeMap(() => this.providerOfAnnualReports.getAnnualReportForBalanceAutoFill$()),
                filter(() => this.currentAnnualReport?.complementaryReports == null),
                untilDestroyed(this),
            )
            .subscribe((annualReportForAutoFill) => this.handleAutoFillAnnualReport(annualReportForAutoFill));

        this.annualReportProvider
            .getPlaceholdersValues$()
            .pipe(untilDestroyed(this))
            .subscribe((value) => {
                this.placeholderMap = value;
            });

        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportSuccessfully), untilDestroyed(this))
            .subscribe(() => this.onSaveSuccessfully());
        this.actionsListener$
            .pipe(ofType(onUpdateAnnualReportFailed), untilDestroyed(this))
            .subscribe(() => this.onSaveFailed());

        this.actionsListener$
            .pipe(
                ofType(
                    printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTronSuccess,
                    printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTronFailed,
                ),
                untilDestroyed(this),
            )
            .subscribe((action) => {
                if (!this.printWindow) {
                    alert('Please disable your Pop-up blocker and try again.');
                    return;
                }

                if (action.type === printAnnualReportUsingPdfTron.printAnnualReportUsingPdfTronSuccess.type) {
                    this.printWindow.location = action.payload.url;
                    if (this.printWindow.closed || typeof this.printWindow.closed === 'undefined') {
                        alert('Please disable your Pop-up blocker and try again.');
                    }
                    return;
                }

                this.printWindow.close();
            });
    }

    private handleGetActiveAnnualReport(activeAnnualReport: IAnnualReport): void {
        this.currentAnnualReport = cloneDeep<IAnnualReport>(activeAnnualReport);
        this.currentComplementaryReports = cloneDeep<IComplementaryReport[]>(activeAnnualReport.complementaryReports);
        this.refreshAopSnapshotData();
    }

    private handleAutoFillAnnualReport(annualReportForAutoFill: IAnnualReport): void {
        this.currentComplementaryReports = cloneDeep<IComplementaryReport[]>(
            annualReportForAutoFill?.complementaryReports,
        );
    }

    override ngAfterViewInit(): void {
        super.ngAfterViewInit();
        this.viewInitialized = true;
        this.scheduleSnapshotPlaceholderUpdate();
    }

    public onComplementaryReportsChanged(complementaryReports: IComplementaryReport[]): void {
        this.complementaryReportsToBeSaved = complementaryReports;
    }

    public onFormsStatusesChanged(formsStatuses: FormControlStatus[]): void {
        const isAllReportsHasTitle = this.complementaryReportsToBeSaved.every((report) => report.title);
        const isFormValid = !formsStatuses.includes(FormStatus.INVALID);
        const shouldDisableProceedButton = !isFormValid || !isAllReportsHasTitle;
        this.disableProceedButton = shouldDisableProceedButton;
        this.footerExtraButtonActions[FIRST_ELEMENT_INDEX].disabled = shouldDisableProceedButton;
    }

    public onPrintComplementaryReportRequested(request: { index: number; fileId?: string }): void {
        const annualReport = this.getAnnualReportForPrint();
        if (annualReport == null || this.currentFinStatements == null) {
            return;
        }

        if (annualReport.complementaryReports?.[request.index] == null) {
            return;
        }

        const pdfTronMapper = this.getPdfTronMapper(annualReport);
        if (pdfTronMapper == null) {
            return;
        }

        const printOptionsForStandalone = this.buildPrintOptionsForStandalone(annualReport, request);
        const annualReportForStandalone = this.complementaryReportPrintService.cloneAnnualReportForStandalone(
            annualReport,
            request,
        );

        const printUsingPdfTronRequestData = this.createPrintUsingPdfTronDto(
            pdfTronMapper,
            annualReportForStandalone,
            this.currentFinStatements,
            printOptionsForStandalone,
        );

        this.complementaryReportPrintService.applyStandaloneSectionToPrintData(
            pdfTronMapper,
            annualReportForStandalone,
            request,
            printUsingPdfTronRequestData,
        );

        this.printWindow = window.open();
        if (this.printWindow != null) {
            this.printWindow.document.body.innerHTML = LOADING_SPINNER_STYLE;
        }

        this.annualReportProvider.dispatchPrintAnnualReportUsingPdfTron(printUsingPdfTronRequestData);
    }

    private getAnnualReportForPrint(): IAnnualReport | undefined {
        if (this.currentAnnualReport == null) {
            return undefined;
        }

        const annualReport = cloneDeep<IAnnualReport>(this.currentAnnualReport);
        if (this.complementaryReportsToBeSaved.length > EMPTY_ARRAY_LENGTH) {
            annualReport.complementaryReports = cloneDeep<IComplementaryReport[]>(this.complementaryReportsToBeSaved);
        } else {
            annualReport.complementaryReports = cloneDeep<IComplementaryReport[]>(this.currentComplementaryReports);
        }

        return annualReport;
    }

    private getPdfTronMapper(annualReport: IAnnualReport): IPdfTronMapper | undefined {
        const businessModel =
            annualReport.annualReportTemplate?.businessModel ?? AnnualReportBusinessModel.OR;

        this.isGermanBusinessModel = businessModel === AnnualReportBusinessModel.HGB;

        return businessModel === AnnualReportBusinessModel.HGB
            ? this.pdfTronGermanMapper
            : this.pdfTronSwissMapper;
    }

    private buildPrintOptionsForStandalone(
        annualReport: IAnnualReport,
        request: { index: number; fileId?: string },
    ): IPrintOptions {
        const basePrintOptions = this.buildBasePrintOptions(annualReport);
        return this.complementaryReportPrintService.buildStandalonePrintOptions(basePrintOptions, request);
    }

    private buildBasePrintOptions(annualReport: IAnnualReport): IPrintOptions {
        const savedPrintOptions = annualReport.printOptions;
        const displayOptions = cloneDeep<IDisplayOptions>(
            savedPrintOptions?.displayOptions ?? this.getDefaultDisplayOptions(annualReport),
        );
        const signatureOptions = cloneDeep(savedPrintOptions?.signatureOptions ?? DEFAULT_SIGNATURE_OPTIONS);
        const styleOptions = this.getStyleOptions(savedPrintOptions);

        return {
            displayOptions,
            signatureOptions,
            styleOptions,
            reportOrder: savedPrintOptions?.reportOrder ?? [],
            reportSectionsTitles: savedPrintOptions?.reportSectionsTitles,
        };
    }

    private getDefaultDisplayOptions(annualReport: IAnnualReport): IDisplayOptions {
        const businessModel =
            annualReport.annualReportTemplate?.businessModel ?? AnnualReportBusinessModel.OR;

        return cloneDeep<IDisplayOptions>(
            businessModel === AnnualReportBusinessModel.HGB
                ? DEFAULT_GERMAN_DISPLAY_OPTIONS
                : DEFAULT_SWISS_DISPLAY_OPTIONS,
        );
    }

    private getStyleOptions(savedPrintOptions: IPrintOptions | undefined): IStyleOptions {
        if (savedPrintOptions?.styleOptions) {
            return cloneDeep<IStyleOptions>(savedPrintOptions.styleOptions);
        }

        const defaults = cloneDeep<IStyleOptions>(DEFAULT_STYLE_OPTIONS);
        if (this.organizationSettings == null) {
            return defaults;
        }

        return {
            ...defaults,
            titleColor: this.organizationSettings.titleColor ?? defaults.titleColor,
            textColor: this.organizationSettings.textColor ?? defaults.textColor,
            backgroundColor: this.organizationSettings.backgroundColor ?? defaults.backgroundColor,
            tableHeaderColor: this.organizationSettings.tableHeaderColor ?? defaults.tableHeaderColor,
            tableFooterColor: this.organizationSettings.tableFooterColor ?? defaults.tableFooterColor,
            backgroundColorPY: this.organizationSettings.backgroundColorPY ?? defaults.backgroundColorPY,
            useCustomColors: false,
        };
    }

    private createPrintUsingPdfTronDto(
        pdfTronMapper: IPdfTronMapper,
        annualReport: IAnnualReport,
        finStatements: IFinStatements[],
        printOptions: IPrintOptions,
    ): PrintUsingPdfTronDto {
        const reportLocaleValue = annualReport.locale ?? this.currentLanguage ?? this.defaultLanguage;
        const reportLocale = reportLocaleValue.toLowerCase();
        const activeLocale = (this.currentLanguage ?? reportLocaleValue).toLowerCase();
        const userLocale = activeLocale;
        const complementaryReportsTitles = (annualReport.complementaryReports ?? []).map(
            (report) => report?.title ?? '',
        );

        const annualReportVisitor = new AnnualReportVisitor(
            reportLocale,
            reportLocale,
            undefined,
            printOptions.reportSectionsTitles,
            this.i18next,
        );
        const predifinedLabels = annualReportVisitor.predifinedLabels;

        const pdfTronInput = pdfTronMapper.mapAnnualReportToPdfTronData(
            annualReport,
            finStatements,
            printOptions,
            this.currentOrganization,
            annualReport,
            DEFAULT_AR_NAVIGATION,
            complementaryReportsTitles,
            GICSCodesUtil.getAllGICSCodesAndSubindustryNames(),
            false,
            predifinedLabels,
            userLocale,
            activeLocale,
            reportLocale,
            null,
            undefined,
        );

        const placeholdersForPrint = this.aopSnapshotService.buildPlaceholdersForPrint(
            annualReport,
            this.placeholderMap,
        );
        const placeholderReplacerVisitor = new PlaceholderReplacerVisitor(placeholdersForPrint, this.dateLocalePipe);
        placeholderReplacerVisitor.visit(pdfTronInput);

        const orderedSectionsTitles = pdfTronInput.sections.map((section) => section.title);
        const orderedSectionsIds = pdfTronInput.sections.map((section) => section.sectionId);

        const sectionIdToTitle = {};
        orderedSectionsIds.forEach((id, index) => {
            sectionIdToTitle[id] = orderedSectionsTitles[index];
        });
        pdfTronInput.sectionIdToTitle = sectionIdToTitle;
        pdfTronInput.orderedSectionsTitles = orderedSectionsTitles;

        let annualReportTemplateId: string | undefined;
        let businessModel: PrintUsingPdfTronDto['businessModel'];
        if (annualReport.annualReportTemplate != null) {
            annualReportTemplateId = annualReport.annualReportTemplate._id;
            businessModel = annualReport.annualReportTemplate.businessModel;
        }

        return {
            data: pdfTronInput,
            orderedSectionsTitles,
            orderedSectionsIds,
            annualReportTemplateId,
            businessModel,
        };
    }

    private refreshAopSnapshotData(): void {
        if (this.currentAnnualReport == null || this.currentFinStatements == null) {
            return;
        }

        if (this.currentAnnualReport.appropriationOfProfit == null) {
            this.appropriationOfProfitSection = undefined;
            this.scheduleSnapshotPlaceholderUpdate();
            return;
        }

        const printOptions = this.buildBasePrintOptions(this.currentAnnualReport);
        const pdfTronMapper = this.getPdfTronMapper(this.currentAnnualReport);
        if (pdfTronMapper == null) {
            return;
        }

        const snapshotData = this.aopSnapshotService.getSnapshotData({
            annualReport: this.currentAnnualReport,
            finStatements: this.currentFinStatements,
            printOptions,
            organization: this.currentOrganization,
            pdfTronMapper,
            currentLanguage: this.currentLanguage,
            defaultLanguage: this.defaultLanguage,
        });

        this.appropriationOfProfitSection = snapshotData.aopSection;
        this.snapshotStyleOptions = snapshotData.snapshotStyleOptions;
        this.snapshotNoPreviousData = snapshotData.snapshotNoPreviousData;
        this.scheduleSnapshotPlaceholderUpdate();
    }

    private scheduleSnapshotPlaceholderUpdate(): void {
        if (!this.viewInitialized) {
            return;
        }

        setTimeout(() => {
            this.updateAopSnapshotHtmlContent();
            this.updateAopSnapshotCurrentYearHtmlContent();
        });
    }

    private updateAopSnapshotHtmlContent(): void {
        if (this.aopSnapshotRef && this.appropriationOfProfitSection != null) {
            if (this.currentAnnualReport == null) {
                this.annualReportProvider.dispatchUpdateAopSnapshotPlaceholder(null);
                return;
            }
            const snapshotHtml = this.aopSnapshotRef.nativeElement.innerHTML as string;
            this.aopSnapshotHtmlContent = this.aopSnapshotService.replaceSnapshotPlaceholders(
                snapshotHtml,
                this.currentAnnualReport,
                this.placeholderMap,
            );
            this.annualReportProvider.dispatchUpdateAopSnapshotPlaceholder(this.aopSnapshotHtmlContent);
            return;
        }

        this.annualReportProvider.dispatchUpdateAopSnapshotPlaceholder(null);
    }

    private updateAopSnapshotCurrentYearHtmlContent(): void {
        if (this.aopSnapshotCurrentYearRef && this.appropriationOfProfitSection != null) {
            if (this.currentAnnualReport == null) {
                this.annualReportProvider.dispatchUpdateAopSnapshotCurrentYearPlaceholder(null);
                return;
            }
            const snapshotHtml = this.aopSnapshotCurrentYearRef.nativeElement.innerHTML as string;
            this.aopSnapshotCurrentYearHtmlContent = this.aopSnapshotService.replaceSnapshotPlaceholders(
                snapshotHtml,
                this.currentAnnualReport,
                this.placeholderMap,
            );
            this.annualReportProvider.dispatchUpdateAopSnapshotCurrentYearPlaceholder(
                this.aopSnapshotCurrentYearHtmlContent,
            );
            return;
        }

        this.annualReportProvider.dispatchUpdateAopSnapshotCurrentYearPlaceholder(null);
    }

    proceedButtonClicked = (): void => {
        this.save();
    };

    override save(): Observable<void> {
        this.mixpanelService.track(MIXPANEL_EVENTS.COMPLEMENTARY_REPORTS_TIME_SPENT_TILL_COMPLETION);
        if (this.currentAnnualReport == null) {
            throw new Error('Current annual report is null');
        }
        this.isSaveRequestInProgress = true;
        this.currentAnnualReport.complementaryReports = this.complementaryReportsToBeSaved;
        this.dispatchSaveAnnualReport(this.currentAnnualReport);
        return of(void 0);
    }

    protected onSaveSuccessfully(): void {
        this.isSaveRequestInProgress = false;
        this.currentComplementaryReports = this.complementaryReportsToBeSaved;

        this.savedSuccessfully.next(true);
        this.fireSuccessNotification();
    }

    private onSaveFailed(): void {
        this.isSaveRequestInProgress = false;
        this.savedSuccessfully.next(false);
        this.fireErrorMessage();
    }

    protected override isDataNewlyCreatedAndSaveable(): boolean {
        if (
            this.currentAnnualReport &&
            this.currentAnnualReport.complementaryReports == null &&
            this.disableProceedButton === false
        ) {
            return true;
        }
        return false;
    }

    protected override isDataChanged(): Observable<boolean> {
        if (this.isSaveRequestInProgress) {
            return this.savedSuccessfully.pipe(map((savedSuccessfully) => !savedSuccessfully));
        } else if (
            this.isAutofillButtonClicked &&
            this.complementaryReportsToBeSaved != null &&
            this.complementaryReportsToBeSaved.length > EMPTY_ARRAY_LENGTH &&
            this.currentAnnualReport.complementaryReports != null
        ) {
            return of(
                !fastDeepEqual(this.currentAnnualReport.complementaryReports, this.complementaryReportsToBeSaved),
            );
        } else if (
            this.currentAnnualReport &&
            this.currentAnnualReport.complementaryReports == null &&
            this.complementaryReportsToBeSaved.length === EMPTY_ARRAY_LENGTH &&
            (this.currentComplementaryReports == null || this.currentComplementaryReports.length === EMPTY_ARRAY_LENGTH)
        ) {
            return of(false);
        } else {
            // Remove undefined / empty properties as fastDeepEqual will return false if one of the objects has an undefined property and the other one doesn't have that property at all
            const cleanObj1 = JSON.parse(JSON.stringify(this.currentComplementaryReports));
            const cleanObj2 = JSON.parse(JSON.stringify(this.complementaryReportsToBeSaved));
            return of(!fastDeepEqual(cleanObj1, cleanObj2));
        }
    }
}